import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import { ThemeContext } from '@/contexts/theme';

import { useMediaQuery, useNavigation } from '@/hooks';
import { get } from '@/utils/iterable/get';

import { THEME_NAME } from '@/constants/theme';
import { VELA_THEME } from '@/constants/user';
import { PAGES } from '@/constants/routes';

import GlobalStyle from '@/styles';

export default function ThemeContextProvider({ children }) {
  const { PAGE } = useNavigation();
  const saved = useSelector(get(['user', VELA_THEME]));
  const autoDetect = saved === THEME_NAME.AUTO;
  const queryMatches = useMediaQuery({ enabled: autoDetect, query: '(prefers-color-scheme: dark)' });
  const name = useMemo(
    function factory() {
      if (PAGE === PAGES.ADMIN) {
        return THEME_NAME.LIGHT;
      }

      if (autoDetect) {
        return queryMatches
          ? THEME_NAME.DARK
          : THEME_NAME.LIGHT;
      }

      switch (saved) {
        case THEME_NAME.DARK:
        case THEME_NAME.LIGHT: {
          return saved;
        }

        default: {
          return THEME_NAME.LIGHT;
        }
      }
    },
    [PAGE === PAGES.ADMIN, autoDetect, queryMatches, saved],
  );

  const value = useMemo(
    function factory() {
      return {
        name,
      };
    },
    [name],
  );

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={value}>
        <GlobalStyle theme={value} />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

ThemeContextProvider.propTypes = {
  children: PropTypes.node,
};
